import React from 'react';
import { motion } from 'framer-motion';
import { ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/outline';

const Testimonials: React.FC = () => {
  return (
    <section id="testimonials" className="relative pt-24 pb-16 px-6 overflow-hidden">
      {/* Subtle ambient background */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          animate={{
            opacity: [0.1, 0.15, 0.1],
          }}
          transition={{ duration: 5, repeat: Infinity }}
          className="absolute top-1/3 left-1/4 w-[300px] h-[300px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(6, 182, 212, 0.4) 0%, transparent 60%)',
            filter: 'blur(60px)',
          }}
        />
        <motion.div
          animate={{
            opacity: [0.08, 0.12, 0.08],
          }}
          transition={{ duration: 7, repeat: Infinity, delay: 1 }}
          className="absolute bottom-1/4 right-1/4 w-[250px] h-[250px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, transparent 60%)',
            filter: 'blur(60px)',
          }}
        />
      </div>

      <div className="max-w-4xl mx-auto relative">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/95 border border-gray-300 text-sm font-semibold mb-6 text-gray-800 shadow-md backdrop-blur"
          >
            <ChatBubbleBottomCenterTextIcon className="w-4 h-4 text-cyan-600" />
            Our Story
          </motion.span>
          <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900`}>
            Why We Built{' '}
            <span className="relative inline-block">
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                }}
              >
                Vidya
              </span>
              <motion.span
                className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                style={{
                  background: 'linear-gradient(90deg, #06B6D4, #3B82F6)',
                }}
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
            </span>
          </h2>
        </motion.div>

        {/* Founder message card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          whileHover={{ y: -4 }}
          className={`rounded-3xl border p-8 md:p-12 relative overflow-hidden border-gray-200`}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* Accent border on top */}
          <div
            className="absolute top-0 left-0 right-0 h-1 rounded-t-3xl"
            style={{
              background: 'linear-gradient(90deg, #06B6D4, #3B82F6)',
            }}
          />

          {/* Glass reflection */}
          <div
            className="absolute inset-0 rounded-3xl opacity-10 pointer-events-none"
            style={{
              background:
                'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 40%)',
            }}
          />

          <div className="relative z-10">
            {/* Message body */}
            <div className={`space-y-6 text-lg leading-relaxed text-gray-600`}>
              <p>
                As students, we were frustrated by how studying always felt disconnected.
                Every session felt like starting over.
                What we'd already read and learned never carried forward.
              </p>

              <p className={`font-semibold text-xl text-gray-800`}>
                Vidya was built to change that.
              </p>

              <p>
                Vidya understands what you've already learned and how ideas connect over time.
                When you ask a question, it answers in context of what you've already learned —
                helping relate new ideas to familiar ones.
              </p>

              <p>
                It doesn't start from zero.
                It builds answers from your understanding, not from scratch.
              </p>

              <p>
                Vidya is designed for{' '}
                <span className="font-semibold bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}>
                  the moments when something almost clicks
                </span>
                {' '}— when one missing connection is holding you back. Instead of rushing to shortcuts,
                it helps you see relationships, patterns, and meaning that actually stick.
              </p>

              <p className={`font-semibold text-lg text-gray-700`}>
                At its core, Vidya is about continuity over resets, clarity over shortcuts, and understanding that lasts.
              </p>

              <p>
                We're building Vidya for everyone who wants learning to feel calmer, more connected, and more human —
                whether you're studying for an exam, revisiting a topic, or learning simply because you're curious.
              </p>
            </div>

            {/* Attribution */}
            <div className="flex items-center gap-4 mt-10 pt-8 border-t border-slate-700/30">
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: 'spring', stiffness: 200, delay: 0.5 }}
                className="text-3xl"
              >
                💙
              </motion.div>
              <div>
                <p className={`font-semibold text-lg text-gray-900`}>
                  — Team Vidya
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
